import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { ChangedImagePrompt, ImagePrompt, ImagePromptWithSize } from '@/app/types';
import { checkSourceImageSize, getImageSize } from '@/app/utils';
import { handleChangeImageRange } from '@/app/utils/client';

interface UseBatchCropOptions {
  maxImageSize: number;
  updateImagePromptByIds: (imagePromptMap: { id: string; prompt: ImagePrompt }[]) => void;
  scene?: UploadScene;
  /**
   * 是否允许临时模式（未登录时裁剪只存本地、不上传，登录后由补传 hook 接手）。
   *
   * 只有在使用方确实挂了补传（useRunTempReferenceImageUploadBatch / useRunTempUploadBatch 等）时才可以开，
   * 否则裁剪结果会永远停在本地、selectedImage 是空串，比直接上传更糟。
   */
  allowTempMode?: boolean;
}

export const useBatchCrop = ({
  maxImageSize,
  updateImagePromptByIds,
  scene = UPLOAD_SCENE.VIDU,
  allowTempMode = false,
}: UseBatchCropOptions) => {
  const t = useTranslations();
  const userInfo = useStore((state) => state.userInfo);
  const [selectModalImageSource, setSelectModalImageSource] = useState<ImagePromptWithSize | null>(null);

  const handleOpenSelectModal = async (idx: number, imagePrompt?: ImagePrompt | null) => {
    if (!imagePrompt) return;

    if (!checkSourceImageSize(imagePrompt)) {
      try {
        const { width, height } = await getImageSize(imagePrompt.sourceImagePreview);
        imagePrompt.sourceImageWidth = width;
        imagePrompt.sourceImageHeight = height;
      } catch {
        toast({
          type: 'error',
          title: t('create.char2VideoForm.imageError', { index: idx + 1 }),
        });
        return;
      }
    }

    setSelectModalImageSource(imagePrompt as ImagePromptWithSize);
  };

  const handleCloseSelectModal = () => setSelectModalImageSource(null);

  //批量保存裁剪图片
  const handleSaveChangedImagePrompts = async (changedImagePrompts: ChangedImagePrompt[]) => {
    if (changedImagePrompts.length === 0) return;
    try {
      const processedPrompts = await Promise.all(
        changedImagePrompts.map(async ({ id, range, cropRatio, imagePrompt }) => {
          const result = await handleChangeImageRange({
            range,
            imagePrompt,
            maxImageSize,
            scene,
            allowTempMode,
            userInfo,
          });
          if (!result) return null;

          // 检查上传错误
          if ('error' in result && result.error) {
            const error = result.error;
            if (error.reason === 'AuditSubmitIllegal') {
              toast({
                type: 'error',
                message: t('create.createForm.upload.error.AuditSubmitIllegal'),
              });
            } else {
              toast({
                type: 'error',
                message: t('popup.toast.uploadingError'),
              });
            }
            return null;
          }

          // TypeScript 类型守卫：此时 result 一定是 { imagePrompt: ImagePrompt }
          if ('imagePrompt' in result) {
            const newImagePrompt = result.imagePrompt;
            if (cropRatio !== undefined) newImagePrompt.cropRatio = cropRatio;
            return { id, prompt: newImagePrompt };
          }

          return null;
        }),
      );
      const validPrompts = processedPrompts.filter((item) => item !== null);
      if (validPrompts.length > 0) updateImagePromptByIds(validPrompts);
    } catch (error) {
      console.error('缓存图片更新失败:', error);
    }
  };

  return {
    selectModalImageSource,
    handleOpenSelectModal,
    handleCloseSelectModal,
    handleSaveChangedImagePrompts,
  };
};
